/**
 * App Version API client.
 * Connects to admin-config-api-serverless via requestAppConfigAdmin.
 */

import { requestAppConfigAdmin } from './auth';
import { getSelectedEnvironment } from '../utils/apiEnvironment';

export const PLATFORMS = ['IOS', 'ANDROID'];
export const RELEASE_STAGES = ['DRAFT', 'IN_REVIEW', 'RELEASED', 'DEPRECATED'];

function buildQuery(params) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') {
      qs.append(k, String(v));
    }
  });
  const str = qs.toString();
  return str ? `?${str}` : '';
}

/**
 * @param {{ page?: number, pageSize?: number, platform?: string, releaseStage?: string, search?: string, includeDeleted?: boolean }} [options]
 * @returns {Promise<{ items: object[], total_count: number, page: number, page_size: number }>}
 */
export async function listAppVersions(options = {}) {
  const { platform, releaseStage, search, includeDeleted, page = 1, pageSize = 10 } = options;
  const query = buildQuery({
    platform: platform && platform !== 'ALL' ? platform : undefined,
    release_stage: releaseStage && releaseStage !== 'ALL' ? releaseStage : undefined,
    search: search || undefined,
    include_deleted: includeDeleted ? 'true' : undefined,
    page,
    page_size: pageSize,
  });
  return requestAppConfigAdmin(`/api/v0/app/versions${query}`);
}

/**
 * Returns all non-deleted app versions (used by policy page dropdowns).
 * Uses the maximum page_size supported by the API.
 * @returns {Promise<object[]>}
 */
export async function listAllAppVersions() {
  const expectedEnvironment = getSelectedEnvironment();
  const items = [];
  for (let page = 1; ; page += 1) {
    const data = await requestAppConfigAdmin(`/api/v0/app/versions?page_size=100${page === 1 ? '' : `&page=${page}`}`, { expectedEnvironment });
    items.push(...(data?.items || []));
    if (!data?.total_count || items.length >= data.total_count || !data?.items?.length) return items;
  }
}

/**
 * @param {object} input
 * @param {{ actorUserId?: string }} [_opts] - ignored; JWT carries the actor identity
 * @returns {Promise<object>}
 */
export async function createAppVersion(input, _opts = {}) {
  return requestAppConfigAdmin('/api/v0/app/versions', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/**
 * @param {number|string} id
 * @param {object} input
 * @param {{ actorUserId?: string }} [_opts]
 * @returns {Promise<object>}
 */
export async function updateAppVersion(id, input, _opts = {}) {
  return requestAppConfigAdmin(`/api/v0/app/versions?id=${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

/**
 * Soft-deletes an app version.
 * @param {number|string} id
 * @param {{ actorUserId?: string }} [_opts]
 * @returns {Promise<{ success: boolean }>}
 */
export async function deleteAppVersion(id, _opts = {}) {
  return requestAppConfigAdmin(`/api/v0/app/versions?id=${id}`, {
    method: 'DELETE',
  });
}

/**
 * Restores a soft-deleted app version via PATCH with deleted_at: "null".
 * @param {number|string} id
 * @param {{ actorUserId?: string }} [_opts]
 * @returns {Promise<object>}
 */
export async function restoreAppVersion(id, _opts = {}) {
  return requestAppConfigAdmin(`/api/v0/app/versions?id=${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ deleted_at: 'null' }),
  });
}
